import React, { useState, useRef, useEffect } from 'react';
import { FaUser } from 'react-icons/fa';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Header.css';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { currentUser } = useAuth();
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileRef]);

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const toggleProfileMenu = () => {
    setProfileOpen(!profileOpen);
  };

  const { logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      setProfileOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const navigateToLogin = () => {
    navigate('/login');
  };

  const navigateToSignup = () => {
    navigate('/signup');
  };

  const navigateToProfile = () => {
    setProfileOpen(false);
    navigate('/profile');
  };

  const navigateToBookings = () => {
    setProfileOpen(false);
    navigate('/bookings');
  };

  // Close mobile menu when location changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <header className="header">
      <div className="header-container">
        <button className={`hamburger${menuOpen ? ' open' : ''}`} onClick={handleMenuToggle} aria-label="Open menu">
          <span className="hamburger-line hamburger-line-top"></span>
          <span className="hamburger-line hamburger-line-mid"></span>
          <span className="hamburger-line hamburger-line-bot"></span>
        </button>
        <div className="logo">
          <img src="/logo192.png" alt="Skyline Backpackers Logo" className="logo-img" />
          <h1>Skyline Backpackers</h1>
        </div>
        <nav className="nav">
          <ul className="nav-list">
            {!currentUser?.isAdmin && (
              <>
                <li><Link to="/" className={location.pathname === '/' ? 'active' : ''}>Home</Link></li>
                <li><a href="#about">About</a></li>
                <li><a href="#services">Services</a></li>
                <li><a href="#contact">Contact</a></li>
              </>
            )}
          </ul>
        </nav>
        <div className="header-actions">
          <div className="profile-container" ref={profileRef}>
            <button className="profile-button" onClick={toggleProfileMenu}>
              {currentUser ? (
                (currentUser.photoURL && currentUser.photoURL !== "null") ? (
                  <img
                    src={currentUser.photoURL}
                    alt="Profile"
                    className="profile-img"
                    style={{
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      objectFit: 'cover', 
                      border: '2px solid #e0e0e0', 
                      background: '#fff'
                    }}
                    onError={(e) => { 
                      e.target.onerror = null; 
                      e.target.style.display = 'none'; 
                    }}
                  />
                ) : (
                  <div className="profile-placeholder">{currentUser ? (currentUser.isAdmin ? "T" : (currentUser.name ? currentUser.name[0] : "")) : "?"}</div>
                )
              ) : (
                <div className="profile-placeholder"><FaUser /></div>
              )}
            </button>
            {profileOpen && (
              <div className="profile-dropdown">
                <div className="profile-dropdown-content">
                  {currentUser ? (
                    <>
                      <div className="profile-info">
                        <div>
                          <p className="profile-name">{currentUser.isAdmin ? 'Tushar Jain' : currentUser.name}</p>
                          <p className="profile-email">{currentUser.email}</p>
                        </div>
                      </div>
                      <div className="profile-menu">
                        {!currentUser.isAdmin && (
                          <>
                            <button className="profile-menu-item" onClick={navigateToProfile}>
                              My Profile
                            </button>
                            <button className="profile-menu-item" onClick={navigateToBookings}>
                              My Bookings
                            </button>
                            <button className="profile-menu-item" onClick={() => {
                              setProfileOpen(false);
                              navigate('/profile/edit');
                            }}>
                              Edit Profile
                            </button>
                          </>
                        )}
                        <button className="profile-menu-item" onClick={handleLogout}>
                          Logout
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="profile-menu">
                      <button className="profile-menu-item" onClick={navigateToLogin}>
                        Login
                      </button>
                      <button className="profile-menu-item" onClick={navigateToSignup}>
                        Sign Up
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      <nav className={`mobile-menu${menuOpen ? ' open' : ''}`}>
        <button className="close-menu" onClick={handleMenuToggle} aria-label="Close menu">
          <span className="close-line close-line1"></span>
          <span className="close-line close-line2"></span>
        </button>
        <ul className="mobile-nav-list">
          {!currentUser?.isAdmin && (
            <>
              <li><Link to="/" onClick={handleMenuToggle}>Home</Link></li>
              <li><a href="#about" onClick={handleMenuToggle}>About</a></li>
              <li><a href="#services" onClick={handleMenuToggle}>Services</a></li>
              <li><a href="#contact" onClick={handleMenuToggle}>Contact</a></li>
            </>
          )}
          
          {/* Mobile Profile Section - Only shown when user is logged in */}
          {currentUser && !currentUser.isAdmin && (
            <>
              <li>
                <div className="mobile-profile-info">
                  <div className="mobile-profile-header">
                    {(currentUser.photoURL && currentUser.photoURL !== "null") ? (
                      <img
                        src={currentUser.photoURL}
                        alt="Profile"
                        className="mobile-profile-img"
                        onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="mobile-profile-placeholder">
                        {currentUser.name ? currentUser.name[0] : ""}
                      </div>
                    )}
                    <div className="mobile-profile-details">
                      <span className="mobile-profile-name">{currentUser.name}</span>
                      <span className="mobile-profile-email">{currentUser.email}</span>
                    </div>
                  </div>
                </div>
              </li>
              <li><Link to="/profile" onClick={handleMenuToggle}>My Profile</Link></li>
              <li><Link to="/bookings" onClick={handleMenuToggle}>My Bookings</Link></li>
              <li><button className="mobile-logout-btn" onClick={() => { handleLogout(); handleMenuToggle(); }}>Logout</button></li>
            </>
          )}
          
          {/* Admin Section - Only shown when admin is logged in */}
          {currentUser && currentUser.isAdmin && (
            <>
              <li>
                <div className="mobile-profile-info">
                  <div className="mobile-profile-header">
                    <div className="mobile-profile-details">
                      <span className="mobile-profile-name">Tushar Jain</span>
                      <span className="mobile-profile-role">Admin</span>
                    </div>
                    <button className="admin-logout-btn" onClick={() => { handleLogout(); handleMenuToggle(); }}>
                      Logout
                    </button>
                  </div>
                </div>
              </li>
            </>
          )}
          
          {/* Login/Signup - Only shown when no user is logged in */}
          {!currentUser && (
            <>
              <li><button className="mobile-auth-link" onClick={() => { navigateToLogin(); handleMenuToggle(); }}>Login</button></li>
              <li><button className="mobile-auth-link signup-link" onClick={() => { navigateToSignup(); handleMenuToggle(); }}>Sign Up</button></li>
            </>
          )}
        </ul>
      </nav>
      {menuOpen && <div className="mobile-menu-backdrop" onClick={handleMenuToggle}></div>}
    </header>
  );
};

export default Header;
